import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plug, ShieldCheck, Loader2, KeyRound, ArrowRight } from "lucide-react";

const STATUS_CLS = {
  CONNECTED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  FILE_EXCHANGE_ACTIVE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  API_ACCESS_REQUIRED: "bg-amber-50 text-amber-700 border-amber-200",
  SETUP_REQUIRED: "bg-amber-50 text-amber-700 border-amber-200",
  OPTIONAL: "bg-slate-100 text-slate-500 border-slate-200",
  NOT_REQUIRED: "bg-slate-100 text-slate-500 border-slate-200",
  NOT_ENABLED: "bg-slate-100 text-slate-400 border-slate-200",
  DISABLED: "bg-slate-100 text-slate-400 border-slate-200",
};
const label = (s) => (s || "").replace(/_/g, " ");

export default function IndiaTaxConnect() {
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [gstin, setGstin] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/india-tax/connectors").then((r) => setData(r.data)).catch(() => {});
    api.get("/india-tax/config").then((r) => { setCfg(r.data); setGstin(r.data.gstin || ""); }).catch(() => {});
  };
  useEffect(load, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/india-tax/config", { gstin, computax_api_key: apiKey || undefined });
      toast.success("Saved"); setApiKey(""); load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="p-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;

  return (
    <div className="max-w-5xl mx-auto p-5 space-y-5" data-testid="india-tax-connect-page">
      <div className="flex items-center gap-2">
        <Plug className="h-6 w-6 text-slate-800" />
        <div>
          <h1 className="text-xl font-bold text-slate-900">ONE CONNECT · India Tax & Compliance</h1>
          <p className="text-sm text-slate-500">Provider-abstracted (CompuTax / authorised GSP). {data.source_of_truth}</p>
        </div>
        <Badge className="ml-auto text-[11px] bg-slate-900 text-white border-slate-900" data-testid="no-cred-badge">
          NO-CREDENTIAL MODE: {data.no_credential_mode}
        </Badge>
      </div>

      {/* operating modes */}
      <div className="grid md:grid-cols-3 gap-3">
        {(data.operating_modes || []).map((m) => (
          <div key={m.key} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`opmode-${m.key}`}>
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-800">{m.label}</div>
              <Badge className={"text-[10px] border " + (STATUS_CLS[m.status] || STATUS_CLS.SETUP_REQUIRED)}>
                {label(m.status)}
              </Badge>
            </div>
            <p className="text-[12px] text-slate-500 mt-1.5">{m.note}</p>
          </div>
        ))}
      </div>

      {/* connector matrix */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 text-sm font-semibold text-slate-700">
          Connector matrix
        </div>
        {data.matrix.map((c) => (
          <div key={c.code} data-testid={`connector-${c.code}`}
            className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-50">
            <div className="min-w-0 flex-1">
              <div className="text-sm text-slate-800">{c.label}</div>
              {c.note && <div className="text-[11px] text-slate-400 truncate">{c.note}</div>}
            </div>
            {c.phase > 0 && <span className="text-[10px] text-slate-400">Phase {c.phase}</span>}
            <Badge className={"text-[10px] border shrink-0 " + (STATUS_CLS[c.status] || STATUS_CLS.SETUP_REQUIRED)}>
              {label(c.status)}
            </Badge>
          </div>
        ))}
      </div>

      {/* config */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="tax-config">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <KeyRound className="h-4 w-4 text-slate-500" /> Provider configuration
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <div className="text-xs text-slate-500 mb-1">Company GSTIN</div>
            <Input value={gstin} onChange={(e) => setGstin(e.target.value)} placeholder="27ABCDE1234F1Z5"
              data-testid="config-gstin" className="h-9" />
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-1">CompuTax / GSP API key (optional)</div>
            <Input value={apiKey} onChange={(e) => setApiKey(e.target.value)} type="password"
              placeholder={cfg?.has_api_key ? "•••••• (configured)" : "Leave blank — file exchange works without it"}
              data-testid="config-apikey" className="h-9" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={save} disabled={saving} data-testid="config-save"
            className="bg-slate-900 hover:bg-slate-800 text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ShieldCheck className="h-4 w-4 mr-1" />} Save
          </Button>
          <a href="/finance/compliance-bridge" data-testid="goto-bridge"
            className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1">
            Open Compliance Bridge <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>
        <p className="text-[11px] text-slate-400">
          A connector is only marked CONNECTED after an authorised API test passes — never faked.
        </p>
      </div>
    </div>
  );
}
